import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Tag } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useEntityTagsQuery } from '@/client/api/tags/tag.query';
import { useUpdateEntityTagsMutation } from '@/client/api/listing-management/entity.mutation';
import { TagPicker } from '@/client/components/admin/tags/TagPicker';

export const TagsTab: React.FC<{ eid: number; etid: number | null }> = ({ eid, etid }) => {
  const entityTags = useEntityTagsQuery(eid, !!eid);
  const updateTagsMut = useUpdateEntityTagsMutation();
  const [tagDraft, setTagDraft] = useState<number[] | null>(null);

  const save = async () => {
    const current = entityTags.data?.tagids ?? [];
    const next = tagDraft ?? [];
    const add = next.filter((id) => !current.includes(id));
    const remove = current.filter((id) => !next.includes(id));
    if (!add.length && !remove.length) {
      setTagDraft(null);
      return;
    }
    try {
      const out = await updateTagsMut.mutateAsync({ eid, add, remove });
      const rejected = out?.rejected_tags ?? out?.data?.rejected_tags;
      const hasReject =
        rejected &&
        Object.values(rejected).some((arr: any) => Array.isArray(arr) && arr.length > 0);
      if (hasReject) {
        toast.warning('Some tags were rejected (inactive, anchor-only, or wrong type).');
      } else {
        toast.success('Tags updated');
      }
      setTagDraft(null);
      entityTags.refetch();
    } catch (err: any) {
      toast.error(err?.message ?? 'Failed to update tags');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-baseline gap-2">
          <h3 className="flex items-center gap-1.5 text-sm font-semibold text-gray-900">
            <Tag className="h-4 w-4 text-gray-500" />
            Tags
          </h3>
          <Link to="/admin/content/tags" className="text-xs text-blue-600 hover:underline">
            Manage catalog
          </Link>
        </div>
        {tagDraft != null && (
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => setTagDraft(null)} disabled={updateTagsMut.isPending}>
              Cancel
            </Button>
            <Button size="sm" disabled={updateTagsMut.isPending} onClick={save}>
              {updateTagsMut.isPending ? 'Saving…' : 'Save tags'}
            </Button>
          </div>
        )}
      </div>

      {/* Current tags, shown by name (label) rather than raw ids. */}
      {entityTags.isLoading ? (
        <Skeleton className="h-8 w-full" />
      ) : (entityTags.data?.tags?.length ?? 0) > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {entityTags.data!.tags.map((t) => (
            <span
              key={t.tagid}
              className="inline-flex items-center rounded-full bg-indigo-50 px-2.5 py-1 text-xs font-medium text-indigo-700"
            >
              {t.name}
            </span>
          ))}
        </div>
      ) : (
        <p className="text-sm text-gray-400">No tags on this listing yet.</p>
      )}

      <div>
        <p className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-400">Edit tags</p>
        {entityTags.isLoading ? (
          <Skeleton className="h-20 w-full" />
        ) : (
          <TagPicker
            value={tagDraft ?? entityTags.data?.tagids ?? []}
            onChange={(ids) => setTagDraft(ids)}
            etid={etid}
            knownTags={entityTags.data?.tags}
          />
        )}
      </div>
    </div>
  );
};
